import {
  ACCENTS,
  BACKGROUNDS,
  BODY_FALLBACK_FAMILY,
  BODY_FALLBACK_FONT,
  CORE_FONTS,
  DEFAULT_THEME_IDS,
  DISPLAY_FONTS,
  PRESETS,
  PRESET_ONLY_BACKGROUNDS,
  PRIMARIES,
} from './tokens';
import { BackgroundLayer, opaqueColors, toCss } from './background';
import {
  Accent,
  Background,
  Font,
  Preset,
  Primary,
  ResolvedTokens,
  PartialThemeIds,
} from './types';

/** Headings are >=24px bold, so the WCAG large-text threshold applies. */
const LARGE_TEXT_RATIO = 3;
/** Accent paints as link text at 13-14px, so no large-text exemption. */
const BODY_TEXT_RATIO = 4.5;
const LIGHT_FG = '#FFFFFF';
const DARK_FG = '#141414';

/** WCAG relative luminance. */
export function lum(hex: string): number {
  const h = (hex || '#000000').replace('#', '');
  const n =
    h.length === 3
      ? h
          .split('')
          .map(c => c + c)
          .join('')
      : h;
  const ch = [0, 2, 4].map(i => {
    const v = parseInt(n.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** WCAG contrast ratio. Order-independent. */
export function ratio(a: string, b: string): number {
  const l1 = lum(a);
  const l2 = lum(b);
  const hi = Math.max(l1, l2);
  const lo = Math.min(l1, l2);
  return (hi + 0.05) / (lo + 0.05);
}

export function toRgb(hex: string): number[] {
  const h = (hex || '#000000').replace('#', '');
  const n =
    h.length === 3
      ? h
          .split('')
          .map(c => c + c)
          .join('')
      : h;
  return [0, 2, 4].map(i => parseInt(n.slice(i, i + 2), 16));
}

export function toHex(rgb: number[]): string {
  return `#${rgb
    .map(v =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/**
 * Blend `over` into `base` by `amount`. Opaque, and computed here rather than
 * with CSS color-mix(), which Safari only supports from 16.2.
 */
export function mix(base: string, over: string, amount: number): string {
  const from = toRgb(base);
  const to = toRgb(over);
  return toHex(from.map((v, i) => v + (to[i] - v) * amount));
}

/** Same colour at an alpha, for overlays that must let the page through. */
export function alpha(hex: string, value: number): string {
  return `rgba(${toRgb(hex).join(',')},${value})`;
}

/** Move a color toward black (negative) or white (positive), keeping hue. */
export function shift(hex: string, amount: number): string {
  const rgb = toRgb(hex);
  return toHex(
    amount < 0
      ? rgb.map(v => v * (1 + amount))
      : rgb.map(v => v + (255 - v) * amount),
  );
}

/**
 * Rule 6.1. Gradients are multi-stop, so keep the lightest colour — the worst
 * case for light text. Read off the layers rather than the rendered CSS: the
 * string is one platform's output, and scraping it made this rule depend on how
 * that output happened to be written.
 */
export function baseHex(bg: Pick<Background, 'layers' | 'mode'>): string {
  const all = opaqueColors(bg.layers);
  if (!all.length) return bg.mode === 'Dark' ? '#000000' : '#FFFFFF';
  return all.reduce((a, b) => (lum(b) > lum(a) ? b : a));
}

/** Rule 6.4. Accent is link text: hold it to 4.5:1, moving lightness not hue. */
export function fitAccent(hex: string, base: string, fallback: string): string {
  if (ratio(hex, base) >= BODY_TEXT_RATIO) return hex;
  const darken = lum(base) > 0.4;
  for (let step = 1; step <= 14; step += 1) {
    const candidate = shift(hex, darken ? -0.06 * step : 0.06 * step);
    if (ratio(candidate, base) >= BODY_TEXT_RATIO) return candidate;
  }
  return fallback;
}

export interface BackgroundTone {
  base: string;
  fg: string;
  dim: string;
  line: string;
  chip: string;
  card: string;
}

/**
 * Rules 6.2 and 6.3. Computed from the base, never from the authored Dark/Light
 * label — that is a picker card label only.
 */
export function bgTone(
  bg: Pick<Background, 'layers' | 'mode'>,
): BackgroundTone {
  const base = baseHex(bg);
  const onWhite = ratio(LIGHT_FG, base);
  const onInk = ratio(DARK_FG, base);
  // Named for the background, not the text. Calling this `light` is what
  // inverted the hairline alphas below.
  const darkBg = onWhite >= onInk;
  const fg = darkBg ? LIGHT_FG : DARK_FG;
  const rgb = darkBg ? '255,255,255' : '20,20,20';
  const head = Math.max(onWhite, onInk);
  const dimAlpha = head >= 9 ? 0.62 : head >= 6 ? 0.75 : 0.9;
  return {
    base,
    fg,
    dim: `rgba(${rgb},${dimAlpha})`,
    // White hairlines read heavier, so they take the lower alpha.
    line: `rgba(${rgb},${darkBg ? 0.14 : 0.2})`,
    chip: `rgba(${rgb},0.09)`,
    // The paper the page is not printed on — near-black lifts a dark page,
    // white raises a light one.
    card: darkBg ? DARK_FG : LIGHT_FG,
  };
}

/** The label colour for a fill: whichever of the two inks reads on it. */
export function onColor(hex: string): string {
  return ratio(LIGHT_FG, hex) >= ratio(DARK_FG, hex) ? LIGHT_FG : DARK_FG;
}

/** Rule 6.5: whether headings can take the primary on this background. */
export function primaryReadable(
  primaryHex: string,
  bg: Pick<Background, 'layers' | 'mode'>,
): boolean {
  return ratio(primaryHex, bgTone(bg).base) >= LARGE_TEXT_RATIO;
}

export type AccentFit = 'kept' | 'shifted' | 'replaced';

/** Rule 6.4 as a verdict: what fitAccent will do to this accent on this background. */
export function accentFit(
  accentHex: string,
  bg: Pick<Background, 'layers' | 'mode'>,
): AccentFit {
  const tone = bgTone(bg);
  const fitted = fitAccent(accentHex, tone.base, tone.fg);
  if (fitted === accentHex) return 'kept';
  return fitted === tone.fg ? 'replaced' : 'shifted';
}

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** A flat colour stored where a palette id goes, for a hand-styled page. */
export function isCustomBackground(id: string | null | undefined): boolean {
  return !!id && HEX.test(id);
}

export function backgroundById(id: string): Background | undefined {
  if (isCustomBackground(id)) {
    // Expanded to six digits so rule 6.1 reads the base off a full hex.
    const color = toHex(toRgb(id));
    const layers: BackgroundLayer[] = [{ type: 'solid', color }];
    return {
      id,
      name: 'Custom',
      mode: ratio(LIGHT_FG, color) >= ratio(DARK_FG, color) ? 'Dark' : 'Light',
      layers,
      css: toCss(layers),
    };
  }

  return (
    BACKGROUNDS.find(b => b.id === id) ||
    PRESET_ONLY_BACKGROUNDS.find(b => b.id === id)
  );
}

export function primaryById(id: string): Primary | undefined {
  return PRIMARIES.find(p => p.id === id);
}

export function accentById(id: string): Accent | undefined {
  return ACCENTS.find(a => a.id === id);
}

export function fontById(id: string): Font | undefined {
  return (
    CORE_FONTS.find(f => f.id === id) || DISPLAY_FONTS.find(f => f.id === id)
  );
}

/** The one font fallback — resolveTheme and the loader must request the same family. */
export function fontOrDefault(id: string | null | undefined): Font {
  return (
    fontById(id || DEFAULT_THEME_IDS.fontId) ??
    (fontById(DEFAULT_THEME_IDS.fontId) as Font)
  );
}

export function presetById(id: string): Preset | undefined {
  return PRESETS.find(p => p.id === id);
}

/**
 * Which preset a theme is, if any. A preset names one combination of the four
 * ids rather than being a fifth choice, so it is derived: setting an option
 * back to its preset value makes the theme that preset again.
 */
export function presetIdFor(ids: PartialThemeIds): string | null {
  const match = PRESETS.find(
    p =>
      p.bg === ids.backgroundId &&
      p.primary === ids.primaryId &&
      p.accent === ids.accentId &&
      p.font === ids.fontId,
  );
  return match?.id ?? null;
}

export function isDisplayFont(id: string): boolean {
  return DISPLAY_FONTS.some(f => f.id === id);
}

/**
 * Rule 6.8. Resolve once per render and pass down — fitAccent's stepping must
 * not yield different accents in different parts of one page.
 */
export function resolveTheme(ids: PartialThemeIds): ResolvedTokens {
  // Empty and unrecognised ids both resolve to the Porter default. Falling back
  // to row 0 instead painted unthemed pages black — row 0 is Ink twice over.
  const slot = <T>(
    lookup: (id: string) => T | undefined,
    id: string | null | undefined,
    fallbackId: string,
  ): T => lookup(id || fallbackId) ?? (lookup(fallbackId) as T);

  const bg = slot(
    backgroundById,
    ids.backgroundId,
    DEFAULT_THEME_IDS.backgroundId,
  );
  const primary = slot(primaryById, ids.primaryId, DEFAULT_THEME_IDS.primaryId);
  const accent = slot(accentById, ids.accentId, DEFAULT_THEME_IDS.accentId);
  const font = fontOrDefault(ids.fontId);
  const tone = bgTone(bg);

  // Rule 6.5 for headings, 6.7 for the button edge — both gate on 3:1.
  // Inline rather than primaryReadable, which would resolve the tone again.
  const readable = ratio(primary.hex, tone.base) >= LARGE_TEXT_RATIO;
  // A primary's heading ink applies only where it clears the same gate;
  // elsewhere headings fall back to the primary itself.
  const headingInk =
    primary.heading && ratio(primary.heading, tone.base) >= LARGE_TEXT_RATIO
      ? primary.heading
      : null;
  const fitted = fitAccent(accent.hex, tone.base, tone.fg);

  return {
    bg: bg.css,
    base: tone.base,
    fg: tone.fg,
    dim: tone.dim,
    line: tone.line,
    heading: headingInk ?? (readable ? primary.hex : tone.fg),
    primary: primary.hex,
    // Rule 6.6 — the swatch ships this, never recompute it from the fill.
    onPrimary: primary.on,
    // Transparent rather than none so the button does not change size.
    primaryEdge: readable ? '1px solid transparent' : `1px solid ${tone.fg}`,
    primaryEdgeColor: readable ? null : tone.fg,
    accent: fitted,
    // The accent is a fill in exactly one place, so its label is computed
    // against the fitted value rather than the page background.
    onAccent: onColor(fitted),
    chip: tone.chip,
    card: tone.card,
    headFont: font.stack,
    bodyFont: isDisplayFont(font.id) ? BODY_FALLBACK_FONT : font.stack,
    headFamily: font.name,
    bodyFamily: isDisplayFont(font.id) ? BODY_FALLBACK_FAMILY : font.name,
    headTrack: font.track,
  };
}
